import { productHref } from "@/shared/routes";
import type { ProductAttribute, ProductBadge, ProductCardModel, ProductDetailModel, SaleRules, VariationGroup } from "./model";
import type { MultiProduct, SingleProduct, SearchHit } from "./dto";

/**
 * DTO da nest → modelo de exibição. Roda SEMPRE no servidor: o cliente recebe o
 * modelo pronto, não o DTO, então o payload RSC não carrega campo que ninguém
 * desenha.
 */

const NO_MAX = Number.MAX_SAFE_INTEGER;

/**
 * Passo/piso de compra vindos da nest. Só o que é INVÁLIDO (ausente, zero,
 * negativo, NaN) cai no default 1 — um passo fracionário (0,5 kg) passa
 * intacto. Não é aqui que se decide se o produto é pesável: o card do DS lê o
 * `unitType` para isso, então um KG com passo default ainda ganha o stepper.
 */
function positiveOr(value: number | null | undefined, fallback: number): number {
    return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : fallback;
}

/**
 * Preço vigente, "de" e % de desconto a partir dos preços da nest. Uma regra só
 * para o card e para cada opção de variação: a opção usava o `price` cru (o
 * "de"), e o card escolhido mostrava "R$ 3,79 · economiza 3%" no lugar de
 * "de R$ 3,79 por R$ 3,69".
 */
function displayPrices(prices: { price: number; priceOffer?: number }) {
    const hasOffer = typeof prices.priceOffer === "number" && prices.priceOffer < prices.price;
    const price = hasOffer ? prices.priceOffer! : prices.price;
    return {
        price,
        oldPrice: hasOffer ? prices.price : undefined,
        discountPercent: hasOffer && prices.price > 0
            ? Math.round(((prices.price - price) / prices.price) * 100)
            : undefined,
    };
}

function variationGroups(product: MultiProduct): VariationGroup[] | undefined {
    const groups = (product.variations ?? [])
        .filter((group) => group.options.length > 0)
        .map((group) => ({
            aggregationId: group.aggregationId,
            name: group.name,
            // Cada opção é um produto inteiro: vai com tudo que o card desenha,
            // para o card exibir a opção escolhida e não só trocar a foto.
            options: group.options.map((option) => ({
                id: String(option.productId),
                label: option.label,
                href: productHref({ slug: option.slug, productId: option.productId }),
                ...displayPrices(option.prices ?? { price: option.price }),
                image: option.image ?? undefined,
                stock: option.stock,
                inStock: option.stock > 0,
                selected: option.selected,
                name: option.name || undefined,
                barcode: option.barcode || undefined,
            })),
        }));

    return groups.length > 0 ? groups : undefined;
}

/**
 * Cor do selo. A nest manda um NOME de cor do tema (`tone`), nunca um hexa — o
 * selo pinta com os tokens da identidade visual da loja, como botões e badges
 * do design system. `bgColor`/`textColor` explícitos (nest antiga ou selo
 * pintado) passam como vieram; sem nada, fica transparente.
 */
const BADGE_TONES = new Set(["primary", "secondary", "accent", "info", "success", "warning", "error", "neutral"]);

export function badgePaint(badge: NonNullable<MultiProduct["badges"]>[number]): ProductBadge {
    const tone = badge.tone?.trim().toLowerCase();
    if (tone && BADGE_TONES.has(tone) && !badge.bgColor) {
        return { name: badge.name, bgColor: `var(--color-${tone})`, textColor: `var(--color-${tone}-content)` };
    }
    return { name: badge.name, bgColor: badge.bgColor ?? "transparent", textColor: badge.textColor ?? "inherit" };
}

export function toProductCard(product: MultiProduct): ProductCardModel {
    const { prices, details, images, rules } = product;

    const { price, oldPrice, discountPercent } = displayPrices(prices);

    return {
        productId: product.productId,
        slug: details.slug,
        href: productHref({ slug: details.slug, productId: product.productId }),
        name: details.name,
        brand: details.brand || undefined,
        barcode: details.barcode || undefined,
        image: images[0] ?? null,
        extraImages: Math.max(0, images.length - 1),
        price,
        oldPrice,
        currency: prices.currency || "BRL",
        discountPercent,
        inStock: rules.stock > 0,
        badges: product.badges?.map(badgePaint),
        footer: product.footer?.trim() || undefined,
        variations: variationGroups(product),
        saleRules: {
            unitType: rules.unitType?.trim() || "UN",
            minPurchase: positiveOr(rules.minPurchase, 1),
            incrementStep: positiveOr(rules.incrementStep, 1),
            maxPurchase: positiveOr(rules.maxPurchase, NO_MAX),
        },
    };
}

/** A nest devolve `specifications` como array OU string crua da coluna. */
function specifications(value: string[] | string | null | undefined): string[] {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value !== "string" || !value.trim()) return [];
    try {
        const parsed: unknown = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.map(String) : [value];
    } catch {
        return [value];
    }
}

/** Ficha de atributos sem grupos vazios — a nest manda o id dos valores, a tela só desenha o nome. */
function attributes(groups: SingleProduct["attributes"]): ProductAttribute[] {
    return (groups ?? [])
        .map((group) => ({
            name: group.name.trim(),
            values: group.values.map((value) => value.name.trim()).filter(Boolean),
        }))
        .filter((group) => group.name && group.values.length > 0);
}

const text = (value: string | null | undefined) => value?.trim() || undefined;

export function toProductDetail(product: SingleProduct): ProductDetailModel {
    const limit = product.rules.purchaseLimitPerCpf;
    return {
        ...toProductCard(product),
        images: product.images,
        description: product.information?.description ?? "",
        shortDescription: product.information?.shortDescription ?? "",
        technicalInformation: product.information?.technicalInformation ?? "",
        specifications: specifications(product.information?.specifications),
        attributes: attributes(product.attributes),
        seoTitle: text(product.seo?.title),
        seoDescription: text(product.seo?.description),
        stock: product.rules.stock,
        alcoholic: product.rules.alcoholic === true,
        allowObservation: product.rules.allowObservation === true,
        purchaseLimitPerCpf: typeof limit === "number" && limit > 0 ? limit : undefined,
    };
}

/** Regras de compra de um hit de busca — as do card quando a nest as manda. */
export function searchHitSaleRules(hit: Pick<SearchHit, "rules" | "unitType">): SaleRules {
    const rules = hit.rules;
    return {
        unitType: (rules?.unitType ?? hit.unitType)?.trim() || "UN",
        minPurchase: positiveOr(rules?.minPurchase, 1),
        incrementStep: positiveOr(rules?.incrementStep, 1),
        maxPurchase: positiveOr(rules?.maxPurchase, NO_MAX),
    };
}

/**
 * Hit da busca → card.
 *
 * A nest devolve o hit como `ProductCardDto` + campos planos, então `rules`
 * vem do catálogo ao vivo. Sem `rules` (índice antigo) fica o MÍNIMO SEGURO
 * (`minPurchase: 1`, sem teto, sem incremento) em vez de valores inventados.
 */
export function searchHitToProductCard(hit: SearchHit): ProductCardModel {
    const hasOffer = typeof hit.offerPrice === "number" && hit.offerPrice < hit.price;
    const price = hasOffer ? hit.offerPrice! : hit.price;

    return {
        productId: hit.productId,
        slug: hit.slug,
        href: productHref({ slug: hit.slug, productId: hit.productId }),
        name: hit.name,
        brand: hit.brand || undefined,
        image: hit.imageUrl ?? null,
        extraImages: 0,
        price,
        oldPrice: hasOffer ? hit.price : undefined,
        currency: "BRL",
        discountPercent: hasOffer && hit.price > 0
            ? Math.round(((hit.price - price) / hit.price) * 100)
            : undefined,
        inStock: hit.hasStock,
        badges: hit.badges?.map(badgePaint),
        footer: hit.footer?.trim() || undefined,
        saleRules: searchHitSaleRules(hit),
    };
}

export interface ProductSection {
    id: string;
    label: string;
    content: string;
    /** `content` é HTML do editor rich-text do portal — precisa de `dangerouslySetInnerHTML`, não de texto puro. */
    html: boolean;
}

/**
 * Seções de texto da PDP, sem as vazias e na ordem de exibição. `specifications`
 * já chega normalizado do `toProductDetail`; aqui é só a montagem.
 *
 * As duas vêm do MESMO editor rich-text do portal (HTML), mesmo `specifications`
 * chegando tipado como array — é sempre um único item com o HTML inteiro
 * (ver `specifications()` em `toProductDetail`, que embrulha a string crua num
 * array de 1 quando o back devolve o texto direto da coluna).
 */
export function productSections(product: ProductDetailModel): ProductSection[] {
    return [
        {
            id: "descricao",
            label: "Descrição",
            content: (product.description || product.shortDescription).trim(),
            html: true,
        },
        {
            id: "informacoes-tecnicas",
            label: "Informações técnicas",
            content: product.technicalInformation.trim(),
            html: true,
        },
        {
            id: "especificacoes",
            label: "Especificações",
            content: product.specifications.join("\n").trim(),
            html: true,
        },
    ].filter((section) => section.content.length > 0);
}
